import { faApplePay } from '@fortawesome/free-brands-svg-icons/faApplePay';
import { faCreditCard } from '@fortawesome/pro-regular-svg-icons/faCreditCard';
import { faCoins } from '@fortawesome/pro-regular-svg-icons/faCoins';
import { ApplePayIcon, Icon } from 'components/payment/quick-checkout/components/payment-methods-selector/styles';
import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { APPLE_PAY, INVOICE, CREDITS } from 'shared/consts/payment-method-types';
import { SavedPaymentMethod } from 'store/payment/types';
import { CreditCycle } from 'store/price/types';
import { getCreditCycleSubtitle, getCreditsAssignedUntilSubtitle } from 'utils/getCreditCycleSubtitle';
import { formatSavedPaymentMethodLabel } from 'utils/formatSavedPaymentMethodLabel';

export const usePaymentMethodsSelector = (
  paymentMethods: Array<SavedPaymentMethod>,
  creditPrice?: number,
  availableCredit?: number,
  creditCycle?: CreditCycle,
  creditsAssignedUntil?: string,
) => {
  const intl = useIntl();

  const buildSavedPaymentLabel = useCallback(
    (paymentMethod: SavedPaymentMethod) => {
      if (paymentMethod.payment_method_type === APPLE_PAY) {
        return intl.formatMessage({ id: 'apple_pay' });
      }
      if (paymentMethod.payment_method_type === INVOICE) {
        return intl.formatMessage({ id: 'invoice' });
      }
      if (paymentMethod.payment_method_type === CREDITS) {
        if (creditPrice) {
          return intl.formatMessage({ id: 'pay_with_credits_generic' }, { credits: creditPrice });
        }
        return intl.formatMessage({ id: 'pay_with_credits_generic' });
      }
      const { capitalizedBrand, formattedExpMonth, formattedExpYear } =
        formatSavedPaymentMethodLabel(paymentMethod) || {};

      return intl.formatMessage(
        {
          id: 'saved_payment_method_option',
        },
        {
          cardBrand: capitalizedBrand,
          cardLastFour: paymentMethod?.card_last_four,
          cardExpMonth: formattedExpMonth,
          cardExpYear: formattedExpYear,
        },
      );
    },
    [intl, creditPrice],
  );

  const getPaymentMethodIcon = useCallback((paymentMethod: SavedPaymentMethod) => {
    if (paymentMethod.payment_method_type === APPLE_PAY) {
      return <ApplePayIcon icon={faApplePay} size="2x" />;
    }
    if (paymentMethod.payment_method_type === INVOICE) {
      return undefined;
    }
    if (paymentMethod.payment_method_type === CREDITS) {
      return <Icon icon={faCoins} />;
    }
    return <Icon icon={faCreditCard} />;
  }, []);

  const buildSubtitle = useCallback(
    (paymentMethod: SavedPaymentMethod) => {
      if (paymentMethod.payment_method_type === CREDITS && creditPrice != null && availableCredit != null) {
        if (creditsAssignedUntil && availableCredit < creditPrice) {
          return getCreditsAssignedUntilSubtitle(creditsAssignedUntil, availableCredit, intl);
        }
        if (creditCycle) {
          return getCreditCycleSubtitle(creditCycle, availableCredit >= creditPrice, availableCredit, intl);
        }
        if (availableCredit < creditPrice) {
          return intl.formatMessage({ id: 'not_enough_credits_subtitle' }, { credits: availableCredit });
        }
        const remainingCredits = availableCredit - creditPrice;
        return intl.formatMessage({ id: 'credits_remaining_after_purchase' }, { credits: remainingCredits });
      }
      return undefined;
    },
    [intl, creditPrice, availableCredit, creditCycle, creditsAssignedUntil],
  );

  return useMemo(
    () =>
      paymentMethods
        .map(paymentMethod => {
          const isCreditsMethod = paymentMethod?.payment_method_type === CREDITS;
          const notEnoughCredits =
            isCreditsMethod && creditPrice != null && availableCredit != null && availableCredit < creditPrice;

          return {
            value: paymentMethod?.id.toString(),
            label: buildSavedPaymentLabel(paymentMethod),
            subtitle: buildSubtitle(paymentMethod),
            icon: getPaymentMethodIcon(paymentMethod),
            disabled: notEnoughCredits,
          };
        })
        .reverse(),
    [paymentMethods, buildSavedPaymentLabel, buildSubtitle, getPaymentMethodIcon, availableCredit, creditPrice, intl],
  );
};
